export interface CommunityStats {
  rankTitle?: string;
  followerCount?: number;
  followingCount?: number;
  postCount?: number;
  weeklyPoints?: number;
  monthlyPoints?: number;
  reputationPoints?: number;
}

export interface UserProfile {
  id?: string;
  _id?: string;
  uid?: string;
  displayName: string;
  email: string;
  photoURL?: string | null;
  coverPhotoURL?: string | null;
  bio?: string;
  apiKey?: string;       // Dùng cho UI (giải mã)
  geminiApiKey?: string; // Dùng lưu trong DB (mã hóa)
  geminiApiKeyMasked?: string; // Khóa API dạng Masked: AIza.....xYz9
  hasGeminiKey?: boolean; // Cờ kiểm tra đã cài đặt Key chưa
  credits: number;
  gradeLevel?: string;
  workplace?: string;
  province?: string;
  role?: 'user' | 'admin';
  roles?: string[];
  emailVerified?: boolean;
  favorites?: string[];
  communityStats?: CommunityStats;
  vipTier?: number;            // 0: thường, 1: VIP, 2: SVIP
  vipExpireAt?: string | Date | null; // Ngày hết hạn VIP
  createdAt?: string;
  updatedAt?: string;
}

export interface TopUpPackage {
  id: string;
  packageId?: string;
  name: string;
  price: number;
  originalPrice?: number;
  discountMsg?: string;
  credits: number;
  description?: string;
  badgeText?: string;
  isActive: boolean;
  order: number;
  type?: 'XU' | 'VIP' | 'SVIP';
  durationDays?: number;
  maxQuantity?: number;
  soldQuantity?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface TopUpRequest {
  id: string;
  _id?: string;
  paymentCode?: string;
  packageName?: string;
  packageType?: 'XU' | 'VIP' | 'SVIP';
  durationDays?: number;
  userId: string;
  userEmail: string;
  userName: string;
  amountMoney: number;
  amountVnd?: number;
  creditsToGain: number;
  credits?: number;
  transferContent: string;
  status: 'WAITING_PAYMENT' | 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  processedAt?: string;
}

export interface TopUpCoupon {
  id: string;
  _id?: string;
  code: string;
  discountPercent: number;
  usageLimit: number;
  usageCount: number;
  expiresAt: string | null;
  isActive: boolean;
  description?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface WalletTransaction {
  id?: string;
  _id?: string;
  amount: number;
  balanceBefore?: number;
  balanceAfter: number;
  type?: string;
  description: string;
  createdAt: string;
}

export interface SystemLedgerTransaction {
  id: string;
  _id?: string;
  ssoUid: string;
  userId?: string;
  userEmail: string;
  userName?: string;
  userAvatar?: string;
  type: string;
  referenceId: string;
  amount: number;
  balanceBefore?: number;
  balanceAfter: number;
  description: string;
  createdAt: string;
}

export interface NavigationProps {
  onBack: () => void;
  onLogout: () => void;
  onSave: (data: UserProfile) => void;
}

export const QUESTION_SUBJECTS = [
  'Âm nhạc',
  'Công nghệ',
  'Địa lí',
  'Đạo đức',
  'Giáo dục công dân',
  'Giáo dục kinh tế và pháp luật',
  'Giáo dục thể chất',
  'Hóa học',
  'Khoa học',
  'Khoa học tự nhiên',
  'Lịch sử',
  'Lịch sử và Địa lí',
  'Mĩ thuật',
  'Nghệ thuật',
  'Ngoại ngữ',
  'Ngữ văn',
  'Sinh học',
  'Tiếng Anh',
  'Tiếng Việt',
  'Tin học',
  'Toán',
  'Tự nhiên và Xã hội',
  'Vật lí',
  'Khác'
] as const;

export type QuestionSubject = typeof QUESTION_SUBJECTS[number];

export const QUESTION_GRADES = [
  '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', 'CĐ/Đại học', 'Khác'
] as const;

export type QuestionGrade = typeof QUESTION_GRADES[number];

export interface QuestionOption {
  id: 'A' | 'B' | 'C' | 'D' | string;
  text: string;
  isCorrect: boolean;
}

export interface QuestionItem {
  id: string;
  _id?: string;
  setId: string;
  userId?: string;
  questionText: string;
  questionMedia?: string;
  options: QuestionOption[];
  explanation?: string;
  difficulty?: 'Nhận biết' | 'Thông hiểu' | 'Vận dụng' | 'Vận dụng cao' | string;
  createdAt?: string;
  updatedAt?: string;
}

export interface QuestionSetUser {
  _id?: string;
  id?: string;
  displayName?: string;
  name?: string;
  avatar?: string;
  photoURL?: string;
  email?: string;
}

export interface QuestionSet {
  id: string;
  _id?: string;
  userId: string | QuestionSetUser;
  userName?: string;
  userEmail?: string;
  userAvatar?: string;
  user?: QuestionSetUser;
  title?: string;
  name: string;
  description?: string;
  subject: QuestionSubject | string;
  grade: QuestionGrade | string;
  isPublic: boolean;
  questionCount?: number;
  cloneCount?: number;
  questions?: QuestionItem[];
  clonedFromId?: string;
  clonedFromName?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface QuestionSetQuota {
  currentSets: number;
  maxSets: number;
  userTier: 'USER' | 'VIP' | 'SVIP';
  canCreateMore: boolean;
}

export interface QuestionItemQuota {
  currentQuestions: number;
  maxQuestions: number;
  userTier: 'USER' | 'VIP' | 'SVIP';
  canAddMore: boolean;
}

// ==================== DASHBOARD STATS TYPES ==================== //
export interface DashboardSummary {
  newUsers: number;
  activeUsers?: number;
  currentVips?: number;
  currentSvips?: number;
  vipUpgrades: number;
  svipUpgrades: number;
  totalMinted: number;
  totalBurned: number;
}

export interface DashboardChartItem {
  date: string;
  newUsers: number;
  activeUsers: number;
  totalMinted: number;
  totalBurned: number;
}

export interface DashboardTopApp {
  appId: string;
  appName?: string;
  totalCalls: number;
  successCount: number;
  errorCount: number;
}

export interface DashboardData {
  summary: DashboardSummary;
  chartData: DashboardChartItem[];
  topApps: DashboardTopApp[];
}
